/**
 * LightAdjustmentPanel Component - Panel with all 6 light adjustment sliders
 * Feature: 011-light-editing
 * Tasks: T025, T026 (exposure/contrast + reset), T090-T093 (Phase 8 tonal adjustments)
 *
 * Provides UI controls for adjusting light parameters:
 * - Exposure and Contrast (basic adjustments)
 * - Highlights, Shadows, Whites, Blacks (tonal range adjustments)
 */

import { useEffect, useCallback } from 'react'
import { AdjustmentSlider } from '../../PhotoEditor/AdjustmentSlider'
import { LightEditData, LIGHT_EDIT_RANGES, DEFAULT_LIGHT_EDIT } from '../../../types/edit.types'
import { SurfaceProvider } from '@/theme/SurfaceContext'

export interface AdjustmentPanelProps {
  /** Current light adjustment values */
  values: LightEditData

  /** Callback when any adjustment changes */
  onChange: (values: LightEditData) => void

  /** Optional CSS class name */
  className?: string
}

/**
 * LightAdjustmentPanel - Control panel for all light adjustments
 *
 * Features:
 * - Exposure slider (-2.0 to +2.0 EV)
 * - Contrast slider (-1.0 to +1.0)
 * - Highlights slider (-100 to +100) - recover/enhance bright areas
 * - Shadows slider (-100 to +100) - open/deepen shadow detail
 * - Whites slider (-100 to +100) - shift white point
 * - Blacks slider (-100 to +100) - shift black point
 * - Reset button to return to defaults
 * - Real-time updates at 60 FPS
 *
 * @example
 * <LightAdjustmentPanel
 *   values={lightData}
 *   onChange={(values) => setLightData(values)}
 * />
 */
export function AdjustmentPanel({
  values,
  onChange,
  className = '',
}: AdjustmentPanelProps): JSX.Element {
  // Handler for individual adjustment changes
  const handleAdjustmentChange = (key: keyof LightEditData, value: number) => {
    onChange({
      ...values,
      [key]: value,
    })
  }

  // Reset all adjustments to defaults (T026)
  const handleReset = useCallback(() => {
    onChange(DEFAULT_LIGHT_EDIT)
  }, [onChange])

  // Check if any adjustments are non-default
  const hasChanges = Object.keys(values).some(
    (key) => values[key as keyof LightEditData] !== DEFAULT_LIGHT_EDIT[key as keyof LightEditData]
  )

  // Keyboard shortcut for reset (R key)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only trigger if R is pressed (not in a text input)
      if (
        event.key === 'r' &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        event.target instanceof HTMLElement &&
        event.target.tagName !== 'INPUT' &&
        event.target.tagName !== 'TEXTAREA' &&
        hasChanges
      ) {
        event.preventDefault()
        handleReset()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasChanges, handleReset])

  return (
    <SurfaceProvider level="surface1" theme="light">
      <div className={`bg-surface-1 p-4 rounded-lg shadow space-y-4 ${className}`}>
        {/* Panel header with reset button */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-foreground">Light Adjustments</h3>
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            className={`text-sm px-3 py-1 rounded ${
              hasChanges
                ? 'bg-muted/20 hover:bg-muted/30 text-foreground'
                : 'bg-muted/10 text-muted cursor-not-allowed'
            } transition-colors`}
            aria-label="Reset all adjustments to default values (keyboard shortcut: R)"
            title="Press R to reset"
          >
            Reset (R)
          </button>
        </div>

      {/* Exposure slider */}
      <AdjustmentSlider
        id="exposure"
        label="Exposure"
        value={values.exposure}
        min={LIGHT_EDIT_RANGES.exposure.min}
        max={LIGHT_EDIT_RANGES.exposure.max}
        step={LIGHT_EDIT_RANGES.exposure.step}
        unit="EV"
        onChange={(value) => handleAdjustmentChange('exposure', value)}
        description="Overall brightness adjustment"
      />

      {/* Contrast slider */}
      <AdjustmentSlider
        id="contrast"
        label="Contrast"
        value={values.contrast}
        min={LIGHT_EDIT_RANGES.contrast.min}
        max={LIGHT_EDIT_RANGES.contrast.max}
        step={LIGHT_EDIT_RANGES.contrast.step}
        onChange={(value) => handleAdjustmentChange('contrast', value)}
        description="Difference between light and dark areas"
      />

        {/* Tonal adjustments (Phase 8) */}
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted font-medium mb-3">Tonal Range</p>

        {/* Highlights slider */}
        <AdjustmentSlider
          id="highlights"
          label="Highlights"
          value={values.highlights}
          min={LIGHT_EDIT_RANGES.highlights.min}
          max={LIGHT_EDIT_RANGES.highlights.max}
          step={LIGHT_EDIT_RANGES.highlights.step}
          onChange={(value) => handleAdjustmentChange('highlights', value)}
          description="Adjust bright areas (negative recovers blown highlights)"
        />

        {/* Shadows slider */}
        <AdjustmentSlider
          id="shadows"
          label="Shadows"
          value={values.shadows}
          min={LIGHT_EDIT_RANGES.shadows.min}
          max={LIGHT_EDIT_RANGES.shadows.max}
          step={LIGHT_EDIT_RANGES.shadows.step}
          onChange={(value) => handleAdjustmentChange('shadows', value)}
          description="Adjust dark areas (positive opens shadow detail)"
        />

        {/* Whites slider */}
        <AdjustmentSlider
          id="whites"
          label="Whites"
          value={values.whites}
          min={LIGHT_EDIT_RANGES.whites.min}
          max={LIGHT_EDIT_RANGES.whites.max}
          step={LIGHT_EDIT_RANGES.whites.step}
          onChange={(value) => handleAdjustmentChange('whites', value)}
          description="Shift white point (positive pushes whites brighter)"
        />

        {/* Blacks slider */}
        <AdjustmentSlider
          id="blacks"
          label="Blacks"
          value={values.blacks}
          min={LIGHT_EDIT_RANGES.blacks.min}
          max={LIGHT_EDIT_RANGES.blacks.max}
          step={LIGHT_EDIT_RANGES.blacks.step}
          onChange={(value) => handleAdjustmentChange('blacks', value)}
            description="Shift black point (negative deepens blacks)"
          />
        </div>
      </div>
    </SurfaceProvider>
  )
}
